/**
 * Adapted from PlayCanvas Engine's gsplat-placement-state-tracker.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type { GSplatPlacement } from '@/plugins/gsplat/unified/GSplatPlacement'

interface GSplatPlacementState {
  readonly formatVersion: number
  readonly modifierHash: number
  readonly numSplats: number
  readonly centersVersion: number
  readonly visible: boolean
  readonly layerMask: number
}

interface GSplatPlacementStatePlanEntry {
  readonly placement: GSplatPlacement
  readonly previous: GSplatPlacementState | undefined
  readonly next: GSplatPlacementState
}

export interface GSplatPlacementStatePlan {
  readonly id: number
  readonly baselineRevision: number
  readonly changed: boolean
  readonly entries: readonly GSplatPlacementStatePlanEntry[]
}

/** Placement 状态变更检测器；detect 零 mutation，World transaction 成功后再提交 baseline。 */
export class GSplatPlacementStateTracker {
  private readonly states = new WeakMap<GSplatPlacement, GSplatPlacementState>()
  private readonly issuedPlans = new WeakSet<GSplatPlacementStatePlan>()
  private readonly committedPlans = new WeakSet<GSplatPlacementStatePlan>()
  private nextPlanId = 1
  private revision = 0

  detectChanges(placements: Iterable<GSplatPlacement>): GSplatPlacementStatePlan {
    const entries: GSplatPlacementStatePlanEntry[] = []
    let changed = false
    for (const placement of placements) {
      const resource = placement.resource
      if (!resource) continue
      const previous = this.states.get(placement)
      const next = Object.freeze({
        formatVersion: resource.format?.extraStreamsVersion ?? 0,
        modifierHash: placement.workBufferModifier?.hash ?? 0,
        numSplats: 'numSplats' in resource ? Number(resource.numSplats ?? 0) : 0,
        centersVersion: 'centersVersion' in resource ? Number(resource.centersVersion ?? 0) : 0,
        visible: isPlacementNodeVisible(placement),
        layerMask: placement.node.layers.mask,
      })
      if (!previous || !statesEqual(previous, next)) changed = true
      entries.push(Object.freeze({ placement, previous, next }))
    }

    const plan = Object.freeze({
      id: this.nextPlanId++,
      baselineRevision: this.revision,
      changed,
      entries: Object.freeze(entries),
    })
    this.issuedPlans.add(plan)
    return plan
  }

  commitChanges(plan: GSplatPlacementStatePlan): void {
    if (!this.issuedPlans.has(plan)) {
      throw new Error('GSplat placement state plan 不属于当前 tracker 或已提交')
    }
    if (plan.baselineRevision !== this.revision) {
      throw new Error('GSplat placement state plan 已过期')
    }
    for (const entry of plan.entries) {
      if (this.states.get(entry.placement) !== entry.previous) {
        throw new Error('GSplat placement state plan baseline 已失效')
      }
    }
    for (const entry of plan.entries) this.states.set(entry.placement, entry.next)
    this.issuedPlans.delete(plan)
    this.committedPlans.add(plan)
    this.revision++
  }

  /** World stage 在后续 publication 失败或调用方 rollback 时恢复 WeakMap baseline。 */
  rollbackChanges(plan: GSplatPlacementStatePlan): void {
    if (!this.committedPlans.has(plan)) return
    if (this.revision !== plan.baselineRevision + 1) {
      throw new Error('GSplat placement state plan 已被后续提交覆盖，不能回滚')
    }
    for (const entry of plan.entries) {
      if (this.states.get(entry.placement) !== entry.next) {
        throw new Error('GSplat placement state rollback baseline 已失效')
      }
    }
    for (const entry of plan.entries) {
      if (entry.previous) this.states.set(entry.placement, entry.previous)
      else this.states.delete(entry.placement)
    }
    this.committedPlans.delete(plan)
    this.revision--
  }
}

function statesEqual(left: GSplatPlacementState, right: GSplatPlacementState): boolean {
  return (
    left.formatVersion === right.formatVersion &&
    left.modifierHash === right.modifierHash &&
    left.numSplats === right.numSplats &&
    left.centersVersion === right.centersVersion &&
    left.visible === right.visible &&
    left.layerMask === right.layerMask
  )
}

function isPlacementNodeVisible(placement: GSplatPlacement): boolean {
  let node: { readonly visible: boolean; readonly parent: unknown } | null = placement.node
  while (node) {
    if (!node.visible) return false
    node = node.parent as typeof node
  }
  return true
}
